<template>
  <div class="ds-page">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">{{ isCompanyView ? "Company" : "Companies" }}</div>
          <h2 class="page-title">EWA Request List</h2>
          <p class="page-subtitle">Monitor Earned Wage Access requests, approval status, and disbursement</p>
        </div>
        <div class="col-auto ms-auto">
          <div class="ewa-header-actions">
            <button class="ds-btn ds-btn--secondary" @click="getList()">
              <i class="el-icon-refresh"></i>
              Refresh
            </button>
            <button class="ds-btn ds-btn--blue" @click="downloadHandler">
              <font-awesome-icon icon="download" />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="ewa-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Filter by date range, status, and search by requestor</span>
        </div>

        <div class="ewa-filter-two-col">
          <!-- Left -->
          <div class="ewa-filter-left">
            <div class="ewa-filter-grid-left">
              <div class="ds-field ewa-field-full" v-if="!isStatusView">
                <label class="ds-field__label">Request Date</label>
                <el-date-picker
                  v-model="search.date"
                  type="daterange"
                  size="small"
                  :picker-options="datePickerOptions"
                  class="w-100"
                  range-separator="→"
                  value-format="yyyy-MM-dd"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                />
              </div>

              <div class="ds-field" v-if="!isStatusView">
                <label class="ds-field__label">Approval Status</label>
                <el-select size="small" v-model="status_filter" class="w-100">
                  <el-option label="All" value="" />
                  <el-option label="Waiting Approval" value="waiting_approval" />
                  <el-option label="Approved" value="approved" />
                  <el-option label="Rejected" value="rejected" />
                </el-select>
              </div>

              <div class="ds-field" v-if="!isStatusView">
                <label class="ds-field__label">Disbursement Status</label>
                <el-select size="small" v-model="disbursement_status_filter" class="w-100">
                  <el-option label="All" value="" />
                  <el-option label="Waiting Approval" value="waiting_approval" />
                  <el-option label="Waiting Disbursement" value="waiting_disbursement" />
                  <el-option label="Completed" value="completed" />
                  <el-option label="Failed" value="failed" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="ewa-filter-sep"></div>

          <!-- Right: search -->
          <div class="ewa-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select size="small" v-model="search.search_by" class="w-100">
                <el-option v-for="(opt, i) in search_options" :key="i" :value="opt.value" :label="opt.text" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Keyword</label>
              <el-input
                clearable
                size="small"
                type="text"
                v-model="search.search_keyword"
                placeholder="Input search keyword..."
                @clear="searchHandler"
              />
            </div>

            <div class="ewa-filter-actions">
              <button @click="resetFilter" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <div class="ds-table-wrap" v-loading="is_loading">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Request Date</th>
              <th v-if="!isCompanyView">Company</th>
              <th>Requestor</th>
              <th>Bank</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Admin Fee</th>
              <th>HR Approval</th>
              <th>Approval Date</th>
              <th>Disbursement</th>
              <th>Disburse Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.created_at_str }}</span>
              </td>
              <td v-if="!isCompanyView">
                <span class="ds-text-bold">{{ item.company_name || "—" }}</span>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.requestor_name)">
                    {{ dsGetInitials(item.requestor_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.requestor_name || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item.requestor_phone_number || "" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div class="ds-user-cell__name">{{ item.bank_account_number || "—" }}</div>
                <div class="ds-user-cell__sub">{{ item.bank_id || "" }}</div>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ item.request_amount_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ item.admin_fee_str }}</span>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.status)">
                  {{ dsStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <span class="ds-text-sm ds-text-muted">
                  {{ item.status === "rejected" ? item.rejected_at_str : item.approved_at_str }}
                </span>
              </td>
              <td>
                <el-tooltip
                  v-if="item.disburse_status === 'failed' && item.failed_reason"
                  :content="item.failed_reason"
                  placement="left"
                >
                  <span class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                    {{ dsStatusLabel(item.disburse_status) }}
                    <i class="el-icon-warning-outline" style="margin-left: 3px"></i>
                  </span>
                </el-tooltip>
                <span v-else class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                  {{ dsStatusLabel(item.disburse_status) }}
                </span>
              </td>
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.disburse_at_str || "—" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <el-empty v-if="!total_rows && !is_loading" description="No EWA requests found" :image="emptyStateImage" class="ds-empty" />

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { GET_DETAIL_EWALIST, GET_ALL_DETAIL_EWALIST } from "@/store/modules/ewa";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import Pagination from "../../components/Pagination.vue";

export default {
  name: "DetailEwaCompany",
  mixins: [designSystemMixin],
  components: { Pagination },
  metaInfo: {
    title: "Earned Wage Access",
  },
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      total_rows: 0,
      page: 1,
      per_page: 20,
      list: [],
      status_filter: "",
      disbursement_status_filter: "",
      search: {
        search_keyword: "",
        search_by: "full_name",
        date: [moment().subtract(1, "month").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
        action: "",
      },
      search_options: [
        { value: "full_name", text: "Requestor Name" },
        { value: "email", text: "Requestor Email" },
        { value: "phone_number", text: "Requestor Phone Number" },
        { value: "company_name", text: "Company Name" },
      ],
      datePickerOptions: {
        disabledDate: (date) => moment(date).isAfter(moment(), "days"),
      },
    };
  },
  computed: {
    isCompanyView() {
      return Boolean(this.$route.params.id);
    },
    isStatusView() {
      return Boolean(this.$route.params.status);
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    buildQuery() {
      const q = {
        disburse_status_filter: this.isStatusView ? this.$route.params.status : this.disbursement_status_filter,
        status_filter: this.isStatusView ? this.$route.query.approval_status : this.status_filter,
        page: this.page,
        search_keyword: this.search.search_keyword,
        search_by: this.search.search_by,
        action: this.search.action,
      };

      if (!this.isStatusView && this.search.date) {
        //q.start_date = this.search.date[0];
        //q.end_date = this.search.date[1];
        q.date = [this.search.date[0], this.search.date[1]];
      }

      if (q.disburse_status_filter === "completed") {
        q.disburse_status_filter = ["completed", "failed"];
      }

      return q;
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const action = this.isCompanyView ? GET_DETAIL_EWALIST : GET_ALL_DETAIL_EWALIST;
        const opt = {
          query: this.buildQuery(),
          ...(this.isCompanyView ? { id: this.$route.params.id } : {}),
        };

        await this.$store.dispatch(action, opt).then((data) => {
          if (this.search.action === "download") {
            const anchor = document.createElement("a");
            anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
            anchor.target = "_blank";
            anchor.download = "Earned_Wage_Report.csv";
            anchor.click();
          }
        });

        if (this.search.action !== "download") {
          const { rows = [], count = 0 } = this.$store.getters.detail_ewa_list || {};
          this.list = rows;
          this.total_rows = count;
        }
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
    searchHandler() {
      this.search.action = "";
      this.page = 1;
      this.getList();
    },
    downloadHandler() {
      this.search.action = "download";
      this.page = 1;
      this.getList();
    },
    resetFilter() {
      this.status_filter = "";
      this.disbursement_status_filter = "";
      this.search.search_keyword = "";
      this.search.search_by = "full_name";
      this.search.date = [moment().subtract(1, "month").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")];
      this.page = 1;
      this.getList();
    },
  },
};
</script>

<style scoped>
/* ── Page Header Actions ─────────────────────────────────── */
.ewa-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Filter Card ─────────────────────────────────────────── */
.ewa-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.ewa-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}
.ewa-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}
.ewa-field-full {
  grid-column: span 2;
}
.ewa-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}
.ewa-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ewa-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

/* ── Table helpers ───────────────────────────────────────── */
.ds-text-sm {
  font-size: 12px;
}
.ds-text-muted {
  color: #8c98a4;
}
</style>
